import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import EditNoteSharpIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import axiosInstance from '../../../Config/axiosInstance';
import AddInternalResourceCost from './AddInternalResourceCost';
import EditInternalResourceCost from './EditInternalResourceCost';
import './InternalResourceCost.css';

function InternalResourceCost() {
  const [canCreate, setCanCreate] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [rows, setRows] = useState([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Format sumă RO
  const formatNumber = (value, locale = 'ro-RO') => {
    if (value === null || value === undefined || value === '') return '';
    const n = Number(value);
    if (Number.isNaN(n)) return '';
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  };

  // Verificare permisiuni
  useEffect(() => {
    const requiredPath = "/internal-resource-cost";
    const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
    const permissions = storedPermissions?.pages || [];

    const hasCreatePermission = permissions.some(page => {
      const pagePath = new URL(page.url).pathname;
      return pagePath === requiredPath && page.can_create === true;
    });
    setCanCreate(hasCreatePermission);

    const hasEditPermission = permissions.some(page => {
      const pagePath = new URL(page.url).pathname;
      return pagePath === requiredPath && page.can_edit === true;
    });
    setCanEdit(hasEditPermission);

    const hasDeletePermission = permissions.some(page => {
      const pagePath = new URL(page.url).pathname;
      return pagePath === requiredPath && page.can_delete === true;
    });
    setCanDelete(hasDeletePermission);
  }, []);

  // Fetch date
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          InternalResourceCostResponse,
          costElementResponse,
          projectCodesResponse,
          InternalResourceResponse
        ] = await Promise.all([
          axiosInstance.get('/internal-resource-cost/'),
          axiosInstance.get('/budget-structure/'),
          axiosInstance.get('/project-codes/'),
          axiosInstance.get('/internal-resources/')
        ]);

        const ProjectCodeMap = projectCodesResponse.data.reduce((acc, code) => {
          acc[code.id] = code.code;
          return acc;
        }, {});

        const costElementMap = costElementResponse.data.reduce((acc, costElement) => {
          acc[costElement.id] = costElement.cost_element_id;
          return acc;
        }, {});

        const InternalResourceMap = InternalResourceResponse.data.reduce((acc, internalResource) => {
          acc[internalResource.id] = internalResource.resource_name;
          return acc;
        }, {});

        const sortedData = InternalResourceCostResponse.data.map(item => ({
          id: item.id,
          project_code: ProjectCodeMap[item.project_code] || "Unknown",
          cost_id: costElementMap[item.cost_id] || "Not Available",
          year: item.year,
          month: item.month,
          internal_resource: InternalResourceMap[item.internal_resource] || "-",
          // IMPORTANT: păstrăm numeric pentru sortare corectă
          hour_worked: item.hour_worked ?? 0,
        })).sort((a, b) => b.id - a.id);

        setRows(sortedData);
      } catch (error) {
        console.error('Eroare la obținerea datelor:', error.response?.data || error.message);
      }
    };

    fetchData();
  }, [location.pathname]);

  // Navigare
  const handleAddClick = () => navigate('/internal-resource-cost/add-internal-resource-cost');
  const handleEditClick = (row) => navigate(`/internal-resource-cost/edit-internal-resource-cost/${row.id}`);

  // Stergere
  const handleDeleteClick = (row) => {
    setItemToDelete(row);
    setOpenDeleteDialog(true);
  };

  // Coloane
  const columns = [
    { field: 'project_code', headerName: 'Project Code', flex: 1.7, minWidth: 260 },
    { field: 'cost_id', headerName: 'Cost ID', flex: 0.7, minWidth: 110 },
    { field: 'year', headerName: 'Year', flex: 0.5, minWidth: 80 },
    { field: 'month', headerName: 'Month', flex: 0.5, minWidth: 80 },
    { field: 'internal_resource', headerName: 'Internal Resource', flex: 1, minWidth: 120 },
    {
      field: 'hour_worked',
      headerName: 'Hour Worked on internal resource',
      flex: 1.2,
      minWidth: 150,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {formatNumber(params.value)}
        </div>
      ),
      // sort numeric explicit (dacă hour_worked vine uneori string)
      sortComparator: (v1, v2) => (Number(v1) || 0) - (Number(v2) || 0),
    },
  ];

  if (canEdit || canDelete) {
    columns.push({
      field: 'action',
      headerName: 'Action',
      width: 120,
      renderCell: (params) => (
        <>
          {canEdit && (
            <IconButton onClick={() => handleEditClick(params.row)} sx={{ color: '#1976d2' }}>
              <EditNoteSharpIcon />
            </IconButton>
          )}
          {canDelete && (
            <IconButton onClick={() => handleDeleteClick(params.row)} sx={{ color: '#d32f2f' }}>
              <DeleteIcon />
            </IconButton>
          )}
        </>
      ),
      sortable: false,
      disableColumnMenu: true,
    });
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          <div className="internal-resource-cost-pappers-page">
            <div className="internal-resource-cost-pappers-container">
              <h2 className='title'>INTERNAL RESOURCE COST </h2>
              {canCreate && (
                <div className="add-button-container">
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAddClick}
                    sx={{ float: 'right', marginBottom: '40px' }}
                  >
                    New cost
                  </Button>
                </div>
              )}
            </div>

            {/* Delete Confirmation Dialog */}
            <Dialog
              open={openDeleteDialog}
              onClose={() => setOpenDeleteDialog(false)}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogTitle id="alert-dialog-title">Confirm Delete</DialogTitle>
              <DialogContent>
                <DialogContentText id="alert-dialog-description">
                  Are you sure you want to delete this record?
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => setOpenDeleteDialog(false)}
                  sx={{
                    backgroundColor: '#e0e0e0',
                    color: '#424242',
                    '&:hover': {
                      backgroundColor: '#bdbdbd',
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    try {
                      await axiosInstance.delete(`/internal-resource-cost/${itemToDelete.id}/`);
                      setRows(prevRows => prevRows.filter(r => r.id !== itemToDelete.id));
                      setOpenDeleteDialog(false);
                    } catch (error) {
                      console.error('Error deleting record:', error.response?.data || error.message);
                    }
                  }}
                  sx={{
                    backgroundColor: '#d32f2f',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#b71c1c',
                    }
                  }}
                  autoFocus
                >
                  Delete
                </Button>
              </DialogActions>
            </Dialog>

            <div className="table-container">
              <DataGrid
                id="internal-resource-table"
                rows={rows}
                columns={columns}
                getRowHeight={() => "auto"}
                pageSize={5}
                initialState={{
                  sorting: {
                    sortModel: [{ field: "id", sort: "desc" }],
                  },
                  pagination: {
                    paginationModel: { pageSize: 12 },
                  },
                }}
                disableRowSelectionOnClick
                sx={{
                  "& .MuiDataGrid-cell": {
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    lineHeight: "1.4",
                    display: "flex",
                    alignItems: "center",
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    whiteSpace: "normal",
                  },
                  "& .MuiDataGrid-cell:focus": {
                    outline: "none",
                  },
                  "& .MuiDataGrid-cell:focus-within": {
                    outline: "none",
                  },
                  "& .Mui-selected:hover": {
                    backgroundColor: "transparent !important",
                  },
                }}
                pageSizeOptions={[10, 12, 20, 25]}
              />
            </div>
          </div>
        }
      />
      <Route path="add-internal-resource-cost" element={<AddInternalResourceCost />} />
      <Route path="edit-internal-resource-cost/:id" element={<EditInternalResourceCost />} />
    </Routes>
  );
}

export default InternalResourceCost;
